import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps a "net weight" display synchronized with the currently selected
 * product variant (e.g. a Pecorino sold in 250g / 500g / 1kg).
 *
 * On a normal product page, VariantPicker#fetchUpdatedSection (variant-picker.js)
 * requests the *whole page* on every variant change — it only scopes the
 * fetch to a single section_id inside quick-add/swatch contexts, neither of
 * which applies here — and resolves the dispatched StandardEvents.productSelect
 * event's `.promise` with `{ detail: { html, productId } }`, where `html` is
 * that full freshly-fetched document (see product-price.js for the same,
 * already-proven consumption pattern). That's what makes this element work
 * even though two copies of it can live in different sections of the same
 * page (the buy-column info pills vs. the provenance card further down):
 * both are just elements somewhere in that one fetched document, so the
 * listener is attached on `document` — not `.closest('.shopify-section')`
 * the way product-price.js does it — since this element and the
 * variant-picker that dispatches the event aren't guaranteed to share a
 * section ancestor, and bubbling only travels up the dispatching element's
 * own ancestor chain.
 *
 * `data-instance` distinguishes the two placements so each only ever
 * replaces itself with its own counterpart; `data-product-id` guards
 * against reacting to an unrelated product's variant change elsewhere on
 * the page (e.g. a quick-add popover on a recommended product).
 */
class ProductVariantWeight extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} event */
  #handleProductSelect = (event) => {
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const newSelf = detail.html.querySelector(
          `product-variant-weight[data-instance="${this.dataset.instance}"][data-product-id="${this.dataset.productId}"]`
        );
        if (newSelf) this.replaceWith(newSelf);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-variant-weight] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-variant-weight')) {
  customElements.define('product-variant-weight', ProductVariantWeight);
}
